import { Check, Plus } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState, type ReactNode } from "react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { normalizeText } from "./filters";

export interface ComboboxOption {
  value: string;
  label: string;
  color?: string;
  count?: number;
}

export interface CreatableComboboxProps {
  /** Options, already in the display order (e.g. most used first). */
  options: ComboboxOption[];
  selected: string[];
  onSelect: (value: string) => void;
  /** Called with the typed text when no option matches it exactly. */
  onCreate?: (label: string) => void;
  placeholder: string;
  children: ReactNode;
  /** Close the popover after a selection (single-value pickers). */
  closeOnSelect?: boolean;
}

/**
 * Searchable list of options that can also create a new option from the typed
 * text. Used by the tag and source pickers.
 */
export const CreatableCombobox = ({
  options,
  selected,
  onSelect,
  onCreate,
  placeholder,
  children,
  closeOnSelect = false,
}: CreatableComboboxProps) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const typed = search.trim();
  const canCreate =
    !!onCreate &&
    typed.length > 0 &&
    !options.some(
      (option) => normalizeText(option.label) === normalizeText(typed),
    );

  const handleSelect = (value: string) => {
    onSelect(value);
    setSearch("");
    if (closeOnSelect) setOpen(false);
  };

  const handleCreate = () => {
    onCreate?.(typed);
    setSearch("");
    if (closeOnSelect) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command
          filter={(value, query) =>
            normalizeText(value).includes(normalizeText(query)) ? 1 : 0
          }
        >
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {!canCreate && (
              <CommandEmpty>
                {translate("crm.operations.combobox.no_results")}
              </CommandEmpty>
            )}
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={
                      selected.includes(option.value)
                        ? "opacity-100"
                        : "opacity-0"
                    }
                  />
                  {option.color && (
                    <span
                      className="size-3 shrink-0 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  <span className="flex-1 truncate">{option.label}</span>
                  {option.count != null && option.count > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {translate("crm.operations.combobox.used", {
                        smart_count: option.count,
                      })}
                    </span>
                  )}
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem
                  value={`__create__${typed}`}
                  onSelect={handleCreate}
                >
                  <Plus />
                  {translate("crm.operations.combobox.create", { name: typed })}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
